import { gql } from "@apollo/client";
import axios from "axios";

export const GET_POLICY_LIST = gql`
  query getAllTypePolicies {
    getAllTypePolicies {
      draftPolicies {
        fileId
        fileStatus
        fileVersion
        fileName
        fileRejectReason
        filePath
        fileStatus
        fileUuid
        is_file_valid
        policyId
        policyName
        policyDisplayName
        policyFileName
        category
        created_by_employee_id
        owner_employee_id
        approver_employee_id
        passingChecksCount
        failingChecksCount
        disabledChecksCount
        pendingChecksCount
        controls {
          controlsId
          controlsName
          controlsDescription
          controlTitle
        }
        frameworkName
        owner {
          role_id
          role_name
          role_description
        }
        approver {
          role_id
          role_name
          role_description
        }
      }
      pendingPolicies {
        fileId
        fileStatus
        fileVersion
        is_file_valid
        policyId
        policyName
        filePath
        policyDisplayName
        policyFileName
        fileUuid
        category
        passingChecksCount
        failingChecksCount
        disabledChecksCount
        pendingChecksCount
        controls {
          controlsId
          controlsName
          controlsDescription
          controlTitle
        }
        frameworkName
        created_by_employee_id
        owner_employee_id
        approver_employee_id
        owner {
          role_id
          role_name
          role_description
        }
        approver {
          role_id
          role_name
          role_description
        }
      }
      activePolicies {
        fileId
        fileStatus
        fileVersion
        is_file_valid
        policyId
        policyName
        filePath
        policyDisplayName
        policyFileName
        fileUuid
        category
        passingChecksCount
        failingChecksCount
        disabledChecksCount
        pendingChecksCount
        controls {
          controlsId
          controlsName
          controlsDescription
          controlTitle
        }
        frameworkName
        created_by_employee_id
        owner_employee_id
        approver_employee_id
        owner {
          role_id
          role_name
          role_description
        }
        approver {
          role_id
          role_name
          role_description
        }
      }
      policiesList {
        policyId
        policyDisplayName
        policyDescription
        policyFileName
        controlsName
        frameworkName
        category
        approver {
          role_id
          role_name
          is_active
          role_description
        }
        owner {
          role_id
          role_name
          is_active
          role_description
        }
      }
    }
  }
`;

export const GET_ACTIVE_POLICY_LIST = gql`
  query getAllTypePolicies {
    getAllTypePolicies {
      activePolicies {
        fileId
        fileStatus
        policyId
        policyName
        filePath
        policyDisplayName
        policyFileName
        fileUuid
        category
        passingChecksCount
        failingChecksCount
        ack
      }
    }
  }
`;

/**
 * Function to upload a file using the REST API.
 * @param {File} file - The file to be uploaded.
 * @param {string} policyId - The policy ID.
 * @param {string} version - The version of the file.
 * @param {string} accessToken - The access token for Authorization.
 * @param {string} idToken - The ID token for Authorization.
 * @returns {Promise} - Axios response.
 */
export const uploadFile = async (
  file,
  policyId,
  version,
  accessToken,
  idToken,
  isCreate
) => {
  const url = `${process.env.REACT_APP_API_BASE_URL}/api/file/upload`;
  const formData = new FormData();
  formData.append("file", file);
  formData.append("policy_id", policyId);
  formData.append("version", version);
  formData.append("fileType", "policy");
  if (!isCreate) {
    formData.append("mode", "update");
  }

  const headers = {
    Authorization: `Bearer ${accessToken}`,
    id_token: idToken,
  };

  try {
    const response = await axios.post(url, formData, { headers });
    return response.data;
  } catch (error) {
    console.error("File upload failed:", error);
    throw error;
  }
};

export const GET_POLICY_APPROVAL_DETAILS = gql`
  query GetPolicyApprovalDetails($file_id: Int!) {
    getPolicyApprovalDetails(file_id: $file_id) {
      approvalDates
      possibleApprovers {
        name
        email
        confidenceScore
        employee {
          id
          employee_id
        }
      }
    }
  }
`;

/**
 * Get a short-lived pre-signed URL for policy file download (recommended, more secure).
 * Backend should: verify auth, generate pre-signed S3/CloudFront URL (e.g. 60–300s expiry), return it.
 * @param {string} filePath - Path to file (e.g. uuid/timestamp.pdf).
 * @param {string} fileName - Suggested download filename (backend may set in signed URL response-content-disposition).
 * @param {string} accessToken - Bearer token.
 * @param {string} idToken - ID token 
 * @returns {Promise<{ url: string }>} - { url: signedUrl }.
 */
export const getPolicyDownloadSignedUrl = async (
  filePath,
  fileName,
  accessToken,
  idToken
) => {
  const baseUrl = (process.env.REACT_APP_API_BASE_URL || "").replace(/\/$/, "");
  const url = `${baseUrl}/api/file/download-url`;
  const headers = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${accessToken}`,
    ...(idToken && { id_token: idToken }),
  };
  const body = JSON.stringify({ filePath, fileName });
  const res = await fetch(url, { method: "POST", headers, body });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const msg = data?.errors?.[0]?.message || data?.message || res.statusText;
    throw new Error(msg);
  }
  if (data?.errors?.length) {
    throw new Error(data.errors[0].message || "Request failed");
  }
  if (!data?.url) throw new Error("Missing url in response");
  return data;
};

// ---------------------------------------------------------------------------
// Rego WatchTower agent binaries
// ---------------------------------------------------------------------------
// Shipping a new agent build is a one-line change: bump AGENT_VERSION and every
// bucket key and download filename below follows. Nothing else references the
// version — the SmartScreen notice imports AGENT_FILE_NAMES rather than
// repeating it.
//
// Note the naming is NOT uniform across platforms, which is why these are two
// separate maps rather than one built from a single pattern:
//   - macOS / Windows use a hyphenated version  (…-1.0.0.pkg)
//   - the Debian package key uses the distro convention (…_1.0.0_amd64.deb)
//     while the file it saves as stays hyphenated
export const AGENT_VERSION = "1.0.0";

// Object keys in the rego-watch-tower-binaries S3 bucket. Must match the
// uploaded objects exactly — S3 keys are case-sensitive, and note "window/"
// is singular.
export const AGENT_BINARY_PATHS = {
  macos_silicon: `macOS/arm64/rego-watch-tower-${AGENT_VERSION}.pkg`,
  macos_intel: `macOS/x64/rego-watch-tower-${AGENT_VERSION}.pkg`,
  windows: `window/rego-watch-tower-${AGENT_VERSION}-setup.exe`,
  linux: `linux/rego-watch-tower_${AGENT_VERSION}_amd64.deb`,
};

// What the browser saves the download as.
export const AGENT_FILE_NAMES = {
  macos_silicon: `rego-watch-tower-${AGENT_VERSION}.pkg`,
  macos_intel: `rego-watch-tower-${AGENT_VERSION}.pkg`,
  windows: `rego-watch-tower-${AGENT_VERSION}-setup.exe`,
  linux: `rego-watch-tower-${AGENT_VERSION}.deb`,
};

/**
 * Get a short-lived pre-signed URL for agent binary download from the
 * "rego-watch-tower-binaries" S3 bucket.
 */
export const getAgentDownloadSignedUrl = async (
  filePath,
  fileName,
  accessToken,
  idToken
) => {
  const baseUrl = (process.env.REACT_APP_API_BASE_URL || "").replace(/\/$/, "");
  const url = `${baseUrl}/api/binary/download-url`;
  const headers = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${accessToken}`,
    ...(idToken && { id_token: idToken }),
  };
  const body = JSON.stringify({ filePath, fileName });
  const res = await fetch(url, { method: "POST", headers, body });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const msg = data?.errors?.[0]?.message || data?.message || res.statusText;
    throw new Error(msg);
  }
  if (data?.errors?.length) {
    throw new Error(data.errors[0].message || "Request failed");
  }
  if (!data?.url) throw new Error("Missing url in response");
  return data;
};

export const UPDATE_POLICY_STATUS = gql`
  mutation UpdatePolicyStatusByUuid(
    $file_uuid: String!
    $status: String!
    $reason: String
    $approval_date: String
    $approver_employee_id: String
  ) {
    updatePolicyStatusByUuid(
      file_uuid: $file_uuid
      status: $status
      reason: $reason
      approval_date: $approval_date
      approver_employee_id: $approver_employee_id
    ) {
      status
      message
    }
  }
`;

export const GENERATE_POLICY_DOWNLOAD_SIGNED_URL = gql`
  mutation GeneratePolicyDownloadSignedUrl {
    generatePolicyDownloadSignedUrl {
      url
    }
  }
`;

export const CREATE_TENANT_DOCUMENT = gql`
  mutation CreateTenantDocument($display_name: String!, $control_ids: [Int]!) {
    createTenantDocument(display_name: $display_name, control_ids: $control_ids) {
      success
      message
      data {
        id
        name
        type
      }
    }
  }
`;

export const DELETE_TENANT_DOCUMENT = gql`
  mutation DeleteTenantDocument($id: Int!) {
    deleteTenantDocument(id: $id) {
      success
      message
      data {
        id
      }
    }
  }
`;

export const ACKNOWLEDGE_POLICY = gql`
  mutation updatePolicyEmployeeAck($file_id: Int!, $ack: Boolean!) {
    updatePolicyEmployeeAck(file_id: $file_id, ack: $ack) {
      success
      message
    }
  }
`;

export const GET_POLICY_CHECKS_STATUS = gql`
  query getPoliciesCheckStatus($fileId: Int!) {
    getFilePolicyCheckStatus(fileId: $fileId) {
      policyId
      fileId
      activeCheckName
      status
      activeCheckId
      statusReason
    }
  }
`;

export const GET_POLICIES_BY_CHECK_ID = gql`
  query getPoliciesByCheckId($activeCheckId: Int!, $audit_id: String) {
    getPoliciesByCheckId(activeCheckId: $activeCheckId, audit_id: $audit_id) {
      fileId
      fileStatus
      fileVersion
      filePath
      fileName
      fileUuid
      policyId
      policyName
      policyDisplayName
      policyFileName
      category
      checkStatus
      checkStatusReason
      approvalDate
    }
  }
`;
